import { Component, EventEmitter, forwardRef, Input, NgZone, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { CustomElement } from '../../classes';
import { MultiTenantService } from '../../../../authentication/services/multi-tenant.service';
import { AuthenticationService } from '../../../../authentication/services/authentication.service';
import { ModalService } from '../../../../modals/services/modal.service';
import { QuickAdditModal } from '../../../../quick-addit/modals/quick-addit.modal';
import { Model } from '../../../../api/model';
import { SelectService } from '../../../form/services/select.service';
import { clone } from '../../../../support/helpers';
import { Subscription } from 'rxjs';

export const CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR: any = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => SelectComponent),
    multi: true
};

type TemplateClosure = (data: { [key: string]: any }, id?: number, text?: string) => string;

type Template = string
    | TemplateClosure;

@Component({
    selector: 'chassis-select',
    templateUrl: './select.component.html',
    styleUrls: ['./select.component.scss'],
    providers: [CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR]
})
export class SelectComponent extends CustomElement implements OnInit, OnDestroy {

    /**
     * The ViewContainerRef instance.
     */
    @ViewChild('select') element;

    @Input() multiple;

    @Input() max = 10;

    @Input() placeholder: string;

    @Input() relation: boolean;

    @Input() options: Array<Object> = [];

    @Input() settings: any = {};

    @Input() ajax: string | Function;
    /**
     * The columns to include in remote response.
     */
    @Input() columns: Array<string>       = ['*'];
    @Input() keyColumn                    = 'key';
    @Input() displayColumn                = 'value';
    @Input() searchable: number | boolean = false;
    @Input() searchKeyColumn              = 'id';
    @Input() searchDisplayColumn          = 'name';
    @Input() searchQueryColumn            = 'name';
    @Input() defaultSearchParams;
    /**
     * The default parameters to include.
     */
    @Input() params: {
        [key: string]: {
            search?: {
                [key: string]: any
            },
            searchJoin?: 'and' | 'or',
            [key: string]: any,
        }
    }                                     = {};
    /**
     * The template for the select options.
     *
     * @link https://select2.org/selections
     *
     * @type {Template}
     */
    @Input() public template: Template;
    /**
     * The template for the select placeholder.
     *
     * @link https://select2.org/placeholders#customizing-placeholder-appearance
     *
     * @type {Template}
     */
    @Input() public placeholderTemplate: Template;
    @Input() entity                       = null;
    @Input() entityDefaults               = {};
    @Input() enableQuickAddit             = false;
    @Input() enableQuickCreate: (model: Model) => boolean;
    @Input() quickCreateDisabledMessage: (model: Model) => string;
    /**
     * The select tags.
     *
     * @link https://select2.org/tagging
     */
    @Input() tags;
    /**
     * The tag constraining callback.
     *
     * @link https://select2.org/tagging#constraining-tag-creation
     */
    @Input() tagsCallback;
    @Input() separator: Array<string>     = [',', ';'];
    @Input() minlength                    = null;
    @Input() maxlength: number            = null;
    @Input('select-first') selectFirst    = false;
    @Output() close                       = new EventEmitter();
    @Output() closing                     = new EventEmitter();
    @Output() open                        = new EventEmitter();
    @Output() opening                     = new EventEmitter();
    @Output() select                      = new EventEmitter();
    @Output() selecting                   = new EventEmitter();
    @Output() unselect                    = new EventEmitter();
    @Output() unselecting                 = new EventEmitter();
    protected headers: Object             = {};
    protected identifier                  = '';
    /**
     * Instance of Select2
     */
    protected selectInstance;

    /**
     * True if the initSelect method is called once
     *
     * @type {boolean}
     */
    protected selectInitialized = false;

    protected transformedOptions = [];

    protected subscription = new Subscription();

    constructor(
        protected zone: NgZone,
        protected authService: AuthenticationService,
        protected tenantService: MultiTenantService,
        protected modalService: ModalService,
        protected selectService: SelectService,
    ) {
        super();
    }

    private get authorizationHeader(): string {
        return 'Bearer ' + this.authService.getToken();
    }

    private get tenantScopeHeader(): string | null {
        if (this.tenantService.isEnabled()) {
            return this.headers['X-TenantScope'] = this.tenantService.getCurrentScope();
        }

        return null;
    }

    public ngOnInit(): void {
        super.ngOnInit();

        this.initSettings();

        setTimeout(() => {
            this.initSelect();
        }, 0);

        this.subscription.add(
            this.initialValue.subscribe(initialValue => {
                this.selectInitialized = false;
                this.initSelect();
            })
        );

        // NOTE: Disable this for multi select as it causes issues
        if (!this.multiple) {
            this.subscription.add(
                this.change.subscribe((value) => {
                    if (this.selectInstance) {
                        this.selectInitialValue(this.selectInstance);
                    }
                })
            );
        }
    }

    public quickAddit(): void {
        if (this.val && this.val.id) {
            const modal = this.modalService.open(QuickAdditModal, {entity: this.entity, entityId: this.val.id});

            modal.result.then((result) => {
                this.val = result.model.plain();
                this.select.emit(this.val);
                this.selectInitialValue(this.selectInstance);
            });
        } else {
            if (this.quickCreateDisabled()) {
                return;
            }

            const modal = this.modalService.open(QuickAdditModal, {
                entity: this.entity,
                entityDefaults: this.entityDefaults
            });

            modal.result.then((result) => {
                this.val = result.model.plain();
                this.select.emit(this.val);
                this.selectInitialValue(this.selectInstance);
            });

        }

    }

    /**
     * Initialize select.
     */
    public initSelect(): void {
        if (!this.selectInitialized) {
            this.selectInitialized = true;

            this.selectInstance = (<any>$(this.element.nativeElement));

            this.settings.data = this.transformedOptions;

            this.selectInstance.select2(this.settings);

            this.selectInitialValue(this.selectInstance);

            if (this.autofocus) {
                this.selectInstance.select2('open').select2('close');
            }

            this.selectInstance.on('select2:select', (event) => {
                this.val = this.selectValue(event);

                // dirty hack for not updating multi select field
                if (this.multiple) {
                    this.change.emit(this.val);
                }
            });

            this.selectInstance.on('select2:unselect', (event) => {
                this.val = this.unselectValue(event) || null;
            });

            this.selectInstance.find('.select2-search--inline .select2-search__field').attr('placeholder', this.placeholder);

            this.initSelectEventEmitters(this.selectInstance);
        }
    }

    public quickCreateDisabled(): boolean {
        if (this.enableQuickCreate === undefined) {
            return false;
        }

        const result = this.enableQuickCreate(
            this.form.value
        );

        if (typeof result === 'boolean') {
            return !result;
        }

        return false;
    }

    public quickCreateDisabledTooltip(): string {
        if (this.quickCreateDisabledMessage === undefined) {
            return '';
        }

        return this.quickCreateDisabledMessage(
            this.form.value
        );
    }

    /**
     * Execute input mutators.
     *
     * @returns {string}
     */
    protected executeMutators(): void {
        super.executeMutators();

        this.tags                = !!this.tags;
        this.multiple            = this.multiple ? true : this.tags;
        this.max                 = this.max ? this.max : null;
        this.ajax                = this.ajax ? this.ajax : null;
        this.keyColumn           = this.keyColumn ? this.keyColumn : 'key';
        this.displayColumn       = this.displayColumn ? this.displayColumn : 'value';
        this.searchKeyColumn     = this.searchKeyColumn ? this.searchKeyColumn : 'id';
        this.searchDisplayColumn = this.searchDisplayColumn ? this.searchDisplayColumn : 'name';
        this.searchQueryColumn   = this.searchQueryColumn ? this.searchQueryColumn : 'name';
        this.defaultSearchParams = this.defaultSearchParams ? this.defaultSearchParams : {};
        this.params              = this.params || this.defaultSearchParams;
        this.searchable          = this.searchable !== undefined && this.searchable !== false ? true : this.ajax ? true : Infinity;
        this.transformedOptions  = this.transformOptions(this.options);
    }

    /**
     * Set initial settings.
     */
    protected initSettings(): void {
        const self = this;

        this.setSetting('allowClear', !this.required);
        this.setSetting('placeholder', this.placeholder);
        this.setSetting('multiple', this.multiple);
        this.setSetting('maximumSelectionLength', this.max);
        this.setSetting('tags', this.tags);

        if (this.tags) {
            this.setSetting('tokenSeparators', [',', ' ']);
        }

        if (this.tagsCallback) {
            this.setSetting('createTag', this.tagsCallback)
        }

        this.setSetting('tokenSeparators', this.separator);
        // this.setSetting('minimumInputLength', 1);
        this.setSetting('maximumInputLength', this.maxlength);

        // Set the basic http headers.
        this.headers['Content-Type'] = 'application/json';
        this.headers['Accept']       = 'application/json';

        // Set the placeholder if tags is disabled.
        if (!this.tags) {
            this.settings.placeholder = {
                id: '',
                placeholder: this.placeholder
            };

            this.settings.templateResult = (data) => {
                return this.getTemplateResult(data);
            };

            this.settings.templateSelection = (data) => {
                return this.getPlaceholderTemplateResult(data);
            };
        }

        // Set the ajax settings.
        if (this.ajax) {
            let currentRequest = null;

            this.settings['delay'] = 300;
            this.settings['ajax']  = {
                url: () => {
                    return this.getUrl()
                },
                cache: true,
                headers: this.headers,
                processResults: (data, params) => {

                    params.page = params.page || 1;

                    const items = self.processResults(data, params);

                    return {
                        results: items,
                        pagination: {
                            more: data.current_page * data.per_page < data.total
                        }
                    };
                },
                transport: (params, success, failure) => {
                    params.beforeSend = (request) => {
                        request.setRequestHeader("Authorization", this.authorizationHeader);

                        if (this.tenantScopeHeader) {
                            request.setRequestHeader("X-TenantScope", this.tenantScopeHeader);
                        }
                    };

                    // Cancel current request
                    if (currentRequest) {
                        currentRequest.abort();
                    }

                    currentRequest = $.ajax(params);

                    return currentRequest
                        .then(success)
                        .fail(failure);
                },
                data: (params: { [key: string]: any }) => {
                    params = clone(params);

                    delete params._type;

                    params.paginate = true;

                    params = Object.assign(params, this.params);

                    const columns = this.searchQueryColumn.split(';');
                    const query   = params.term;

                    params.search = this.selectService.search(
                        query,
                        columns,
                        this.params.search,
                    );

                    params.columns = this.columns;

                    params.page = params.page || 1;

                    return params;
                },
                error: (jqXHR, textStatus, errorThrown) => {
                    if (jqXHR.status === 401) {
                        // Refresh token when necessary.
                        this.authService.refreshToken()
                            .subscribe();
                    }

                    if (textStatus !== 'abort') {
                        // Todo: automatic resend the request
                        console.warn('Token has been renewed, please try again.')
                    }
                }
            };
        }
    }

    protected getTemplateResult(data: any): JQuery<HTMLElement> | any {
        // Display the placeholder when no value was provided.
        if (!data.id) {
            return data.placeholder;
        }

        if (this.template) {
            // When a template string is given the string is parsed.
            if (typeof this.template === 'string') {
                return $(this.parseTemplateString(this.template, data));
            }

            // When a template function is given the function value is parsed.
            if (typeof this.template === 'function') {
                return $(this.template(data.raw, data.id, data.text));
            }
        }

        if (data.text) {
            return data.text;
        }

        return data.placeholder;
    }

    protected getPlaceholderTemplateResult(data: any): JQuery<HTMLElement> | any {
        // Display the placeholder when no value was provided.
        if (!data.id) {
            return data.placeholder;
        }

        if (this.placeholderTemplate) {
            // When a template string is given the string is parsed.
            if (typeof this.placeholderTemplate === 'string') {
                return $(this.parsePlaceholderTemplateString(data));
            }

            // When a template function is given the function value is parsed.
            if (typeof this.placeholderTemplate === 'function') {
                return $(this.parsePlaceholderTemplateFunction(data));
            }
        }

        if (data.text) {
            return data.text;
        }

        return data.placeholder;
    }

    protected parsePlaceholderTemplateFunction(data: any): string {
        if (this.ajax) {
            data = Object.assign({}, data.raw);

            if (!data.raw) {
                data.raw = this.val || {};
            }
        }

        if (typeof this.placeholderTemplate === 'function') {
            return this.placeholderTemplate(data.raw, data.id, data.text);
        }

        return '';
    }

    protected parsePlaceholderTemplateString(data: any): any {
        if (this.ajax) {
            data     = Object.assign({}, data.raw);
            data.raw = this.val;
        }

        return this.parseTemplateString(this.placeholderTemplate, data);
    }

    protected parseTemplateString(template, item): any {
        return template.replace(/\${([a-zA-Z0-9.\_]*)}/gi, (match, group) => {
            try {
                const val = eval('item.' + group);

                return val === null ? '' : val;
            } catch (exception) {
                return '';
            }
        });
    }

    /**
     * Process the results of an Ajax request.
     *
     * @param data
     * @param params
     * @returns {Array}
     */
    protected processResults(data, params): any[] {
        if (this.relation) {
            data = data['data'];
        }

        const items = [];

        for (const item of data) {
            if (!(this.val instanceof Array) || this.val instanceof Array && (this.val).indexOf(item[this.searchKeyColumn]) < 0) {
                items.push({
                    id: item[this.searchKeyColumn] !== undefined ? item[this.searchKeyColumn] : '',
                    text: item[this.searchDisplayColumn] ? item[this.searchDisplayColumn] : '',
                    raw: item
                });
            }
        }

        return items;
    }

    /**
     * Set a setting.
     *
     * @param {string} key
     * @param value
     */
    protected setSetting(key: string, value: any): void {
        this.settings[key] = value;
    }

    /**
     * Transform the data to a structure our select will understand.
     *
     * @param {Array<Object>} options
     * @returns {Array<Option>}
     */
    protected transformOptions(options: Array<Object>): Array<Option> {
        const array = [];

        if (options) {
            for (const option of options) {
                array.push({
                    id: option[this.keyColumn] !== undefined ? option[this.keyColumn] : '',
                    text: option[this.displayColumn] ? option[this.displayColumn] : '',
                    raw: option instanceof Model ? option.plain() : option
                });
            }
        }

        return array;
    }

    /**
     * Set the initial value.
     *
     * @param select
     */
    protected selectInitialValue(select): boolean {
        // when a select box uses ajax calls and the model value is an object
        // -> it displays the default selected value(s)
        if (this.val && (this.val.id || this.val.length) && this.ajax) {

            if (this.multiple) {
                for (const item in this.val) {
                    if (this.val.hasOwnProperty(item)) {
                        select.append('<option value="' + this.val[item][this.searchKeyColumn] +
                            '" selected>' + this.val[item][this.searchDisplayColumn] + '</option>');
                    }
                }
            } else {
                select.append('<option value="' + this.val[this.searchKeyColumn] + '" selected>' +
                    this.val[this.searchDisplayColumn] + '</option>');
            }

            select.trigger('change');

        } else {

            if (this.tags) {
                if (this.val instanceof Array) {
                    for (let item of this.val) {
                        select.append('<option value="' + item + '" selected>' + item + '</option>');
                    }
                }
                return false;
            }

            // If the firstSelect attribute is set -> select the first option by default
            if (this.val === undefined && this.selectFirst !== false) {
                this.val = this.options[0][this.searchKeyColumn];
            }

            // options are given by default (static options)
            // we can simply set the default based on the id
            select.val(this.val, true).trigger('change');
        }
    }

    /**
     * Action to perform when selecting a value.
     *
     * @param value
     * @returns {any}
     */
    protected selectValue(event): any[] | any {

        if (this.readonly) {
            return;
        }

        if (this.multiple) {

            const object = this.tags ? event.params.data.id : event.params.data.raw;

            const array = this.val instanceof Array ? this.val : this.val ? [this.val] : [];
            array.push(object);

            return array;

        } else if (this.ajax) {

            const object = event.params.data.raw;
            return object;

        }

        return event.target.value;
    }

    /**
     * Action to perform when unselecting a value.
     *
     * @param value
     * @returns {any}
     */
    protected unselectValue(value): string | any[] {
        if (this.readonly) {
            return;
        }

        if (this.multiple) {
            const array = this.val instanceof Array ? this.val : [this.val];
            array.splice(this.val.indexOf(value), 1);
            return array;
        }

        return '';
    }

    /**
     * Set event emitters.
     *
     * @param select
     */
    protected initSelectEventEmitters(select): void {
        select.on('select2:close', (event) => {
            this.close.emit(event);
        });

        select.on('select2:closing', (event) => {
            this.closing.emit(event);
        });

        select.on('select2:open', (event) => {
            this.open.emit(event);
        });

        select.on('select2:opening', (event) => {
            // Re-set the url
            this.settings.ajax = this.getUrl();

            this.opening.emit(event);
        });

        select.on('select2:select', (event) => {
            if (this.ajax) {
                this.select.emit(event.params.data.raw);
            } else {
                this.select.emit(event.target.value);
            }
        });

        select.on('select2:selecting', (event) => {
            this.selecting.emit(event);
        });

        select.on('select2:unselect', (event) => {
            this.unselect.emit(event.target.value);
        });

        select.on('select2:unselecting', (event) => {
            this.unselecting.emit(event);
        });

        select.on('change', (event) => {
        });
    }

    /**
     * Returns the calll to the backend.
     *
     * @returns {any}
     */
    private getUrl(): string {
        if (typeof this.ajax === 'function') {
            this.settings['ajax'] = this.ajax();

            return this.ajax();
        }

        this.settings['ajax'] = this.ajax;

        return this.ajax;
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

}

export interface Option {
    id: string;
    text: string;
}



// WEBPACK FOOTER //
// ./libs/chassis-core/src/app/forms/elements/components/select/select.component.ts